import React, { useCallback, useMemo } from 'react';
import { Box, Card, CardContent, CardHeader, Divider, Typography } from '@mui/material';
import './my-account.style.scss';
import Grid from '@mui/material/Unstable_Grid2';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { AccountEditData, AccountEditPassword, AccountState, accountState, } from './account-state';
import AvatarCard from '../../component/avatar/avatar-card';
import { IMAGE_UPLOAD_SPECS } from '../../utils/image-upload-spec';
import Tab from '../../component/tab/tab.component';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import { TopBarState } from '../../component/top-bar/top-bar-state';
import WaitButton from '../../component/button/waitButton.component';
import { useTranslation } from 'react-i18next';
import ResellerAccount from '../reseller/reseller-account.page';
import { ResellerAccountState } from '../reseller/reseller-account-state';
import { ReactComponent as IonPersonCircleIcon } from '../../static/images/svg/ion_person-circle.svg';
import { ReactComponent as MdiPasswordIcon } from '../../static/images/svg/mdi_password.svg';
import MyAccountFormInput, { MyAccountSelectOption } from './my-account-form-input.component';

export default observer(function MyAccountPage() {

    /** 多语言函数，切换语言时 Tab 等文案随 i18n 更新 */
    const { t } = useTranslation();
    /** 顶部 Tab 配置：文案与设计稿 my_account_tab_* 一致 */
    const config = useMemo(() => ({
        panel: [
            {
                title: <Box className='FCC my-account-page__tab-title'>
                    <IonPersonCircleIcon className='my-account-page__tab-icon' />
                   
                        {t('my_account_tab_my_account')}
                   
                </Box>
            },
            {
                title: <Box className='FCC my-account-page__tab-title'>
                    <MdiPasswordIcon className='my-account-page__tab-icon' />
                  
                        {t('my_account_tab_change_password')}
                </Box>
            },
        ]
    }), [t]);
    React.useEffect(() => {
        accountState.init();
        const topbarState = MobxObjectsManager.get(TopBarState);
        topbarState.updateMyAccountTitle();
        return () => { }
    }, [])

    // 经销商账号数据管理
    const useAccountData = useLocalObservable(() => new ResellerAccountState({
        belongedResellerId: accountState.userInfo?._id,
    }));

    const Content = useCallback(() => {
        switch (accountState.panelActive) {
            case 0: return accountState.accountType === 'RESELLER' ?
                <ResellerAccount
                    componentSource='RESELLER_RESELLER_DETAIL'
                    useResellerAccountMobx={useAccountData}
                />
                :
                <AccountProfileDetails useData={accountState} />;
            case 1: return <SecurityDetails useData={accountState} />;
            default: return <></>;
        }
    }, [accountState.panelActive, useAccountData]);

    return (
        <Box className='my-account-page-container'>
            <Tab
                data={config.panel}
                activeIndex={accountState.panelActive}
                itemStyle={{ marginRight: 4 }}
                containerStyle={{ marginBottom: '1rem' }}
                onClick={(index: number) => accountState.updatePanel(index)}
            />

            <Content />
        </Box>
    )
})

/**
 * 信息详情
 */
const AccountProfileDetails = observer(({ useData }: { useData: AccountState }) => {
    const { t } = useTranslation();
    // 账户资料表单项（标签文案与设计稿 my_account_field_* 一致）
    const fromData: { key: keyof AccountEditData, label: string, inputType?: 'INPUT' | 'SELECT' | 'DATE', items?: MyAccountSelectOption[] }[] = [
        { key: 'firstName', label: t('my_account_field_name'), inputType: 'INPUT' },
        { key: 'lastName', label: t('my_account_field_surname'), inputType: 'INPUT' },
        { key: 'birthday', label: t('my_account_field_date'), inputType: 'DATE' },
        {
            key: 'gender', label: t('my_account_field_gender'), inputType: 'SELECT',
            items: [{ value: '0', label: t('person_page_button_male') }, { value: '1', label: t('person_page_button_female') }]
        },
    ]
    const [nowDate,] = React.useState(new Date())
    return (
        <Card className='my-account-page'>
            <CardHeader
                title={
                    <Typography variant='h3' component='div' sx={{ color: 'var(--text-dark)' }}>
                        {t('my_account_title_profile')}
                    </Typography>
                }
            />
            <CardContent className='my-account-page__avatar-content'>
                <Box className='pic-box base-shadow'>
                    <AvatarCard
                        size={120}
                        aspectRatio={1}
                        uploadSpec={IMAGE_UPLOAD_SPECS.square}
                        avatarUrl={useData.logoUrl ? useData.logoUrl : useData.accountEditData?.avatar}
                        onChangeAvatarUrl={(avatarUrl: string, file: File) => {
                            useData.updateLogofile(avatarUrl, file, true)
                        }}
                    />
                </Box>
            </CardContent>
            {useData.accountType === 'STAFF' ? (
                <CardContent className='my-account-page__contact-qr-content'>
                    <Typography variant='h3' component='div' className='my-account-page__contact-qr-title'>
                        {t('my_account_menu_store_contact_title')}
                    </Typography>
                    <Box className='my-account-page__contact-qr-frame'>
                        <AvatarCard
                            size={160}
                            aspectRatio={1}
                            uploadSpec={IMAGE_UPLOAD_SPECS.square}
                            avatarUrl={useData.contactQrUrl ?? useData.contactQrRemoteUrl ?? undefined}
                            onChangeAvatarUrl={(previewUrl: string, file: File) => {
                                useData.updateContactQrFile(previewUrl, file, true);
                            }}
                        />
                    </Box>
                </CardContent>
            ) : null}
            <Divider />
            <CardContent className='my-account-page__form-content'>
                <Grid container spacing={2} className='my-account-page__form-grid' >
                    {fromData.map((item, index) => {
                        const v = useData.accountEditData[item.key] || '';
                        return <Grid sm={6} key={index} xs={12}>
                            <MyAccountFormInput
                                inputType={item.inputType ?? 'INPUT'}
                                label={item.label}
                                value={v}
                                options={item.inputType === 'SELECT' ? item.items : undefined}
                                onChange={(nextValue) => {
                                    useData.updateAccountEditData(Object.assign({}, useData.accountEditData, { [item.key]: nextValue }), true)
                                }}
                                maxDate={nowDate}
                            />
                        </Grid>
                    })}
                </Grid>
            </CardContent>
            <CardContent className='my-account-page__actions'>
                <WaitButton
                    disabled={!(useData.operate.info || useData.operate.logo || useData.operate.contactQr)}
                    onClick={async () => await useData.save()}
                    titleText={''} >
                    {t('general_text_save')}
                </WaitButton>
            </CardContent>
        </Card >
    )
})

/**
 * 修改密码
 */
const SecurityDetails = observer(({ useData }: { useData: AccountState }) => {
    const { t } = useTranslation();
    // 修改密码表单项（标签含冒号，与设计稿 my_account_password_* 一致）
    const fromData: { key: keyof AccountEditPassword, label: string }[] = [
        { key: 'oldPassword', label: t('my_account_password_old') },
        { key: 'newPassword', label: t('my_account_password_new') },
        { key: 'confirmPassword', label: t('my_account_password_confirm') },
    ]
    return (
        <Card className='my-account-page'>
            <CardHeader
                title={
                    <Typography variant='h3' component='div' sx={{ color: 'var(--text-dark)' }}>
                        {t('my_account_title_change_password')}
                    </Typography>
                }
            />
            <CardContent className='my-account-page__form-content'>
                <Grid container spacing={1} className='my-account-page__form-grid my-account-page__form-grid--security' >
                    {fromData.map((item, index) => {
                        const v = useData.accountEditPassword[item.key] || '';
                        return <Grid sm={12} key={index} marginBottom={2} >
                            <MyAccountFormInput
                                inputType='PASSWORD'
                                value={v}
                                label={item.label}
                                onChange={(nextValue) => {
                                    useData.updateAccountEditPassword(Object.assign({}, useData.accountEditPassword, { [item.key]: nextValue }))
                                }}
                            />
                        </Grid>
                    })}
                </Grid>
            </CardContent>
            <CardContent className='my-account-page__actions'>
                <WaitButton onClick={async () => { await useData.savePassword() }} titleText={''} >
                    {t('general_text_save')}
                </WaitButton>
            </CardContent>
        </Card>
    )
})